import { createContext, useCallback, useContext, useMemo } from 'react';
import type { ClientId } from '@seaber/common/lib/clientId';
import type { CalculatePnlResult } from '../pnl';
import type { Co2Emission, EuEtsCost, FuelEuCost } from '../co2/co2Cost';
import type { TimeData } from '../../core/TimeData';
import type { PnlProcessorContextInput } from '../context/context';

export type CalculateTimeDataPnl = (
  tds: TimeData[],
  context: Pick<PnlProcessorContextInput, 'vessel' | 'type'>,
) => CalculatePnlResult;

export type Pnl = Pick<CalculatePnlResult, 'costs' | 'errors' | 'income'>;
export type PnlByShipmentId = Map<ClientId, Pnl>;

export interface PnlContextValue {
  calculateTimeDataPnl: CalculateTimeDataPnl;
  emissions: {
    co2: {
      all: Co2Emission[];
      byShipmentId: Map<ClientId, Co2Emission[]>;
    };
  };
  /** Pnl calculation result for the current selected scenario */
  pnl: {
    all: Pnl;
    byShipmentId: PnlByShipmentId;
  };
}

export const PnlContext = createContext<PnlContextValue>({
  calculateTimeDataPnl: () => ({
    costs: [],
    errors: [],
    extras: {
      co2Processor: {
        co2Emissions: [],
      },
    },
    income: [],
  }),
  pnl: {
    all: { costs: [], errors: [], income: [] },
    byShipmentId: new Map(),
  },
  emissions: {
    co2: {
      all: [],
      byShipmentId: new Map(),
    },
  },
});

function usePnlContext() {
  const context = useContext(PnlContext);

  if (!context) {
    console.error('usePnlContext used outside of PnlContext Provider');
    return undefined;
  }

  return context;
}

export function useCalculateTimeDataPnl() {
  const context = usePnlContext();

  if (!context) {
    throw new Error('useCalculateTimeDataPnl used outside of PnlProvider');
  }

  return context.calculateTimeDataPnl;
}

export function useCurrentScenarioPnl() {
  const context = usePnlContext();

  return context?.pnl;
}

export function useGetFuelEuCostsByShipmentId() {
  const context = usePnlContext();

  return useCallback(
    (shipmentId: ClientId): FuelEuCost[] => {
      const pnl = context?.pnl.byShipmentId.get(shipmentId);
      if (!pnl) return [];

      return pnl.costs.filter((cost) => cost.category === 'fuel_eu_cost');
    },
    [context?.pnl.byShipmentId],
  );
}

export function useGetEuEtsCostsByShipmentId() {
  const context = usePnlContext();

  return useCallback(
    (shipmentId: ClientId): EuEtsCost[] => {
      const pnl = context?.pnl.byShipmentId.get(shipmentId);
      if (!pnl) return [];

      return pnl.costs.filter((cost) => cost.category === 'eu_ets_cost');
    },
    [context?.pnl.byShipmentId],
  );
}

export function useGetCo2EmissionsByShipmentId() {
  const context = usePnlContext();

  return useCallback(
    (shipmentId: ClientId): Co2Emission[] => {
      return context?.emissions.co2.byShipmentId.get(shipmentId) ?? [];
    },
    [context?.emissions.co2.byShipmentId],
  );
}

export function useCo2ProcessorErrors() {
  const context = usePnlContext();

  const co2ProcessorErrors = useMemo(() => {
    return (
      context?.pnl.all.errors.filter((e) => e.processor === 'co2-cost') ?? []
    );
  }, [context?.pnl.all.errors]);

  return co2ProcessorErrors;
}
